<template>
  <div
    class="min-h-screen flex flex-col"
    :class="useCampusLayout ? 'bg-[#f7f7f7]' : 'bg-lightGray'"
  >
    <nav
      class="fixed top-0 left-0 right-0 z-40 flex items-center h-12 min-h-[3rem] bg-white border-b border-gray-200 px-4"
    >
      <router-link :to="useCampusLayout ? '/home' : '/'" class="flex items-center shrink-0">
        <img src="/campus_logo.svg" alt="Campus" class="h-7" />
      </router-link>

      <div
        v-if="!useCampusLayout"
        id="topbar-search"
        class="ml-auto flex-1 max-w-3xl mx-4 hidden md:flex items-center"
      />

      <div class="flex items-center gap-2 ml-auto">
        <template v-if="!useCampusLayout">
          <button
            v-if="isGuide"
            :title="isBlurred ? 'Show sensitive data' : 'Hide sensitive data'"
            class="flex items-center justify-center hover:bg-gray-100 rounded-full w-8 h-8"
            @click="toggleBlur"
          >
            <EyeOff v-if="isBlurred" class="w-4.5 h-4.5 text-gray-500" />
            <Eye v-else class="w-4.5 h-4.5 text-gray-500" />
          </button>
        </template>
        <ProfileDropdown />
      </div>
    </nav>

    <!-- Non-legacy: CampusSidebar | Legacy: SidebarMenu -->
    <CampusSidebar v-if="useCampusLayout" />
    <SidebarMenu v-else class="z-40" />

    <div
      class="pt-12 flex-grow flex flex-col transition-[padding-left] duration-300 ease-in-out"
      :class="isSidebarCollapsed ? 'md:pl-16' : 'md:pl-60'"
    >
      <div class="flex-grow">
        <router-view />
      </div>
      <DashboardFooter v-if="useCampusLayout" />
    </div>

    <button
      class="md:hidden fixed bottom-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-white border border-gray-200 rounded-full shadow-md"
      @click="isMobileSidebarOpen = !isMobileSidebarOpen"
      aria-label="Toggle Sidebar"
    >
      <Menu class="w-5 h-5 text-gray-600" />
    </button>

    <div
      v-if="isMobileSidebarOpen"
      class="md:hidden fixed inset-0 bg-black/30 z-30"
      @click="isMobileSidebarOpen = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, provide, onMounted, computed } from 'vue'
import { Menu, Eye, EyeOff } from 'lucide-vue-next'
import { useLocalStorage } from '@vueuse/core'
import ProfileDropdown from '@/components/common/ProfileDropdown.vue'
import CampusSidebar from '@/components/layout/CampusSidebar.vue'
import SidebarMenu from '@/components/layout/SidebarMenu.vue'
import DashboardFooter from '@/components/layout/DashboardFooter.vue'
import { useCampusStore } from '@/stores/campus'
import { useAuth } from '@/composables/useAuth'
import { usePrivacyMode } from '@/composables/usePrivacyMode'

const campusStore = useCampusStore()
const { isGuide } = useAuth()
const { isBlurred, toggleBlur } = usePrivacyMode()

const hasCurriculum = ref<boolean | null>(null)
const hasActiveExpedition = ref<boolean | null>(null)

const useCampusLayout = computed(() => {
  if (hasCurriculum.value === null) return true
  if (hasCurriculum.value) return true
  // No personal curriculum: use CampusLayout only if NOT in active expedition (setup flow)
  if (hasActiveExpedition.value === null) return true
  return !hasActiveExpedition.value
})

const isMobileSidebarOpen = ref(false)
const isSidebarCollapsed = useLocalStorage('sidebar-collapsed', false)
provide('isMobileSidebarOpen', isMobileSidebarOpen)
provide('isSidebarCollapsed', isSidebarCollapsed)

onMounted(async () => {
  try {
    hasCurriculum.value = await campusStore.checkHasPersonalCurriculum()
  } catch {
    const userStore = (await import('@/stores/user')).useUserStore()
    hasCurriculum.value = Boolean((userStore.currentUser as any)?.has_personal_curriculum)
  }
  if (hasCurriculum.value === false) {
    try {
      const userStore = (await import('@/stores/user')).useUserStore()
      const userId = userStore.currentUser?.id
      if (userId) {
        const { useExpeditionsStore } = await import('@/stores/expeditions')
        hasActiveExpedition.value = await useExpeditionsStore().checkHasActiveExpedition(userId)
      } else {
        hasActiveExpedition.value = false
      }
    } catch {
      hasActiveExpedition.value = false
    }
  } else {
    hasActiveExpedition.value = false
  }
})
</script>
